import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import Swal from "sweetalert2";
import axios from "axios";
import "./AddTenantSubscription.css";
import { API_BASE } from "../../../ApiUrl";
import SubscriptionTabs from "./SubscriptionTabs";

const API_URL = `${API_BASE}/tenant-subscriptions/`;
const TENANT_RECORDS_API_URL = `${API_BASE}/tenant-records/`;

const PLAN_OPTIONS = [
  { value: "PILOT", label: "Pilot" },
  { value: "STANDARD", label: "Standard" },
  { value: "ENTERPRISE", label: "Enterprise" }
];

const STATUS_OPTIONS = [
  { value: "TRIAL", label: "Trial" },
  { value: "ACTIVE", label: "Active" },
  { value: "PAST_DUE", label: "Past Due" },
  { value: "SUSPENDED", label: "Suspended" },
  { value: "CANCELLED", label: "Cancelled" },
  { value: "EXPIRED", label: "Expired" }
];

const emptySubscription = {
  id: null,
  plan: "",
  status: "TRIAL",
  start_date: "",
  renewal_date: "",
  expiry_date: "",
  seat_limit: "",
  project_limit: "",
  storage_limit_gb: "",
  used_seats: "0",
  used_projects: "0",
  used_storage_gb: "0"
};

const toDateInput = (value) => (value ? String(value).substring(0, 10) : "");

const toNumberInput = (value, fallback = "") =>
  value !== null && value !== undefined ? String(value) : fallback;

const getPlanLabel = (value) =>
  PLAN_OPTIONS.find((p) => p.value === value)?.label || value || "-";

const getStatusLabel = (value) =>
  STATUS_OPTIONS.find((s) => s.value === value)?.label || value || "-";

const AddTenantSubscription = () => {
  const navigate = useNavigate();

  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState([{ ...emptySubscription }]);

  const [tenantId, setTenantId] = useState(null);
  const [tenantUser, setTenantUser] = useState(null);

  // ---- Read tenant user ----
  useEffect(() => {
    try {
      const raw = localStorage.getItem("tenant_user");
      const parsed = raw ? JSON.parse(raw) : null;
      setTenantUser(parsed);
      if (parsed?.tenant_id) setTenantId(Number(parsed.tenant_id));
    } catch (error) {
      console.error("Error reading tenant_user:", error);
    }
  }, []);

  // ---- Fetch list ----
  useEffect(() => {
    if (tenantId) fetchSubscriptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);

      const response = await axios.post(
        TENANT_RECORDS_API_URL,
        {
          tenant_id: tenantId,
          tables: ["tenant_subscriptions"]
        },
        { headers: { "Content-Type": "application/json" } }
      );

      const result = response.data;

      let rows = [];
      if (result?.data?.tenant_subscriptions) {
        rows = result.data.tenant_subscriptions;
      } else if (result?.tenant_subscriptions) {
        rows = result.tenant_subscriptions;
      } else if (Array.isArray(result?.data)) {
        rows = result.data;
      } else if (Array.isArray(result)) {
        rows = result;
      }

      setSubscriptions(rows);
    } catch (error) {
      console.error("Error fetching tenant subscriptions:", error);
      Swal.fire({
        icon: "error",
        title: "Unable to load subscriptions",
        text:
          error.response?.data?.detail ||
          "Something went wrong while loading subscriptions."
      });
    } finally {
      setLoading(false);
    }
  };

  // ---- Form handlers (per row) ----
  const handleChange = (index, e) => {
    const { name, value } = e.target;
    setFormData((prev) =>
      prev.map((sub, i) => (i === index ? { ...sub, [name]: value } : sub))
    );
  };

  const handleAdd = () => {
    setFormData([{ ...emptySubscription }]);
    setIsEditing(false);
    setEditingId(null);
    setShowForm(true);
  };

  const handleAddAnother = () => {
    setFormData((prev) => [...prev, { ...emptySubscription }]);
  };

  const handleRemoveForm = (index) => {
    setFormData((prev) => prev.filter((_, i) => i !== index));
  };

  const handleEdit = (sub) => {
    setFormData([
      {
        id: sub.id,
        plan: sub.plan || "",
        status: sub.status || "TRIAL",
        start_date: toDateInput(sub.start_date),
        renewal_date: toDateInput(sub.renewal_date),
        expiry_date: toDateInput(sub.expiry_date),
        seat_limit: toNumberInput(sub.seat_limit),
        project_limit: toNumberInput(sub.project_limit),
        storage_limit_gb: toNumberInput(sub.storage_limit_gb),
        used_seats: toNumberInput(sub.used_seats, "0"),
        used_projects: toNumberInput(sub.used_projects, "0"),
        used_storage_gb: toNumberInput(sub.used_storage_gb, "0")
      }
    ]);
    setEditingId(sub.id);
    setIsEditing(true);
    setShowForm(true);
  };

  const handleDelete = async (sub) => {
    const confirm = await Swal.fire({
      icon: "warning",
      title: "Delete Subscription?",
      text: `Are you sure you want to delete the "${getPlanLabel(
        sub.plan
      )}" subscription?`,
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626"
    });

    if (!confirm.isConfirmed) return;

    try {
      await axios.delete(`${API_URL}${sub.id}/`);

      Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Subscription deleted successfully.",
        timer: 1500,
        showConfirmButton: false
      });

      fetchSubscriptions();
    } catch (error) {
      console.error("Error deleting subscription:", error);
      Swal.fire({
        icon: "error",
        title: "Delete failed",
        text:
          error.response?.data?.detail ||
          "Unable to delete the subscription."
      });
    }
  };

  const extractError = (error) => {
    if (error.response?.data) {
      const d = error.response.data;
      if (typeof d === "string") return d;
      if (d.detail) return d.detail;
      if (d.message) return d.message;
      if (d.errors) {
        return Object.entries(d.errors)
          .map(
            ([field, errors]) =>
              `${field}: ${Array.isArray(errors) ? errors.join(", ") : errors}`
          )
          .join(" | ");
      }
      return JSON.stringify(d);
    }
    return error.message || "Unknown error";
  };

  // ---- Validation per row ----
  const validateRow = (sub, index) => {
    const label = `Subscription ${index + 1}`;

    const isEmptyRow =
      !sub.plan &&
      !sub.status &&
      !sub.start_date &&
      (sub.seat_limit === "" || sub.seat_limit === undefined) &&
      !sub.id;
    if (isEmptyRow) return true;

    if (!sub.plan) {
      Swal.fire({
        icon: "warning",
        title: "Required Field Missing",
        text: `Please select a Plan for ${label}.`
      });
      return false;
    }

    if (!sub.status) {
      Swal.fire({
        icon: "warning",
        title: "Required Field Missing",
        text: `Please select a Status for ${label}.`
      });
      return false;
    }

    if (!sub.start_date) {
      Swal.fire({
        icon: "warning",
        title: "Required Field Missing",
        text: `Please provide a Start Date for ${label}.`
      });
      return false;
    }

    if (sub.seat_limit === "" || Number(sub.seat_limit) < 0) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Seat Limit",
        text: `Seat Limit for ${label} must be a non-negative number.`
      });
      return false;
    }

    if (sub.project_limit !== "" && Number(sub.project_limit) < 0) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Project Limit",
        text: `Project Limit for ${label} must be a non-negative number.`
      });
      return false;
    }

    if (sub.storage_limit_gb !== "" && Number(sub.storage_limit_gb) < 0) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Storage Limit",
        text: `Storage Limit for ${label} must be a non-negative number.`
      });
      return false;
    }

    if (sub.used_seats !== "" && Number(sub.used_seats) < 0) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Used Seats",
        text: `Used Seats for ${label} must be a non-negative number.`
      });
      return false;
    }

    if (sub.used_projects !== "" && Number(sub.used_projects) < 0) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Used Projects",
        text: `Used Projects for ${label} must be a non-negative number.`
      });
      return false;
    }

    if (sub.used_storage_gb !== "" && Number(sub.used_storage_gb) < 0) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Used Storage",
        text: `Used Storage (GB) for ${label} must be a non-negative number.`
      });
      return false;
    }

    if (sub.renewal_date && sub.start_date && sub.renewal_date < sub.start_date) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Dates",
        text: `Renewal Date for ${label} cannot be before its Start Date.`
      });
      return false;
    }

    if (sub.expiry_date && sub.start_date && sub.expiry_date < sub.start_date) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Dates",
        text: `Expiry Date for ${label} cannot be before its Start Date.`
      });
      return false;
    }

    return true;
  };

  const buildPayload = (sub) => ({
    plan: sub.plan,
    status: sub.status,
    start_date: sub.start_date || null,
    renewal_date: sub.renewal_date || null,
    expiry_date: sub.expiry_date || null,
    seat_limit: Number(sub.seat_limit),
    project_limit: sub.project_limit === "" ? null : Number(sub.project_limit),
    storage_limit_gb:
      sub.storage_limit_gb === "" ? null : Number(sub.storage_limit_gb),
    used_seats: sub.used_seats === "" ? 0 : Number(sub.used_seats),
    used_projects: sub.used_projects === "" ? 0 : Number(sub.used_projects),
    used_storage_gb:
      sub.used_storage_gb === "" ? 0 : Number(sub.used_storage_gb),
    tenant: tenantId,
    user: tenantUser?.user_id
  });

  // ---- Submit ----
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!tenantId) {
      Swal.fire({
        icon: "error",
        title: "Tenant ID missing",
        text: "Unable to identify the current tenant."
      });
      return;
    }

    for (let i = 0; i < formData.length; i++) {
      if (!validateRow(formData[i], i)) return;
    }

    const activeRows = formData.filter(
      (sub) =>
        sub.plan ||
        sub.start_date ||
        sub.seat_limit !== "" ||
        sub.id
    );

    const CONFIG = { headers: { "Content-Type": "application/json" } };

    try {
      setLoading(true);

      if (isEditing) {
        await axios.put(
          `${API_URL}${editingId}/`,
          buildPayload(formData[0]),
          CONFIG
        );

        await Swal.fire({
          icon: "success",
          title: "Updated Successfully",
          timer: 1500,
          showConfirmButton: false
        });
      } else {
        for (const sub of activeRows) {
          await axios.post(API_URL, buildPayload(sub), CONFIG);
        }

        await Swal.fire({
          icon: "success",
          title: "Created Successfully",
          text: `${activeRows.length} subscription${
            activeRows.length > 1 ? "s" : ""
          } created successfully.`,
          timer: 1500,
          showConfirmButton: false
        });
      }

      setShowForm(false);
      setIsEditing(false);
      setEditingId(null);
      setFormData([{ ...emptySubscription }]);

      await fetchSubscriptions();
    } catch (error) {
      console.error("Error saving subscription:", error);

      Swal.fire({
        icon: "error",
        title: isEditing ? "Update failed" : "Creation failed",
        text: extractError(error)
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setIsEditing(false);
    setEditingId(null);
    setFormData([{ ...emptySubscription }]);
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="tenant-subscription-page">
          <SubscriptionTabs />

          {!showForm ? (
            <>
              <div className="tenant-subscription-page-header">
                <div>
                  <h2>Tenant Subscriptions</h2>
                </div>

                <button
                  type="button"
                  className="tenant-subscription-add-top-btn"
                  onClick={handleAdd}
                >
                  + Add Subscription
                </button>
              </div>

              <div className="tenant-subscription-list-card">
                {loading ? (
                  <div className="tenant-subscription-loading">
                    Loading subscriptions...
                  </div>
                ) : subscriptions.length === 0 ? (
                  <div className="tenant-subscription-empty">
                    <div className="tenant-subscription-empty-icon">+</div>
                    <h3>No Subscriptions Found</h3>
                    <p>
                      There are no subscription records available for the
                      current tenant.
                    </p>
                    <button
                      type="button"
                      className="tenant-subscription-empty-add-btn"
                      onClick={handleAdd}
                    >
                      + Add Subscription
                    </button>
                  </div>
                ) : (
                  <div className="tenant-subscription-table-wrapper">
                    <table className="tenant-subscription-table">
                      <thead>
                        <tr>
                          <th>Plan</th>
                          <th>Status</th>
                          <th>Start Date</th>
                          <th>Renewal Date</th>
                          <th>Expiry Date</th>
                          <th>Seat Limit</th>
                          <th>Project Limit</th>
                          <th>Storage (GB)</th>
                          <th>Usage</th>
                          <th>Actions</th>
                        </tr>
                      </thead>

                      <tbody>
                        {subscriptions.map((sub) => (
                          <tr key={sub.id}>
                            <td>
                              <span className="tenant-subscription-plan-pill">
                                {getPlanLabel(sub.plan)}
                              </span>
                            </td>

                            <td>
                              <span
                                className={`tenant-subscription-status-pill status-${String(
                                  sub.status || ""
                                ).toLowerCase()}`}
                              >
                                {getStatusLabel(sub.status)}
                              </span>
                            </td>

                            <td className="tenant-subscription-muted">
                              {sub.start_date || "-"}
                            </td>

                            <td className="tenant-subscription-muted">
                              {sub.renewal_date || "-"}
                            </td>

                            <td className="tenant-subscription-muted">
                              {sub.expiry_date || "-"}
                            </td>

                            <td className="tenant-subscription-muted">
                              {sub.seat_limit ?? "-"}
                            </td>

                            <td className="tenant-subscription-muted">
                              {sub.project_limit ?? "-"}
                            </td>

                            <td className="tenant-subscription-muted">
                              {sub.storage_limit_gb ?? "-"}
                            </td>

                            <td className="tenant-subscription-muted">
                              {sub.used_seats ?? 0}/{sub.seat_limit ?? "-"}{" "}
                              seats · {sub.used_projects ?? 0}/
                              {sub.project_limit ?? "-"} projects ·{" "}
                              {sub.used_storage_gb ?? 0}/
                              {sub.storage_limit_gb ?? "-"} GB
                            </td>

                            <td>
                              <div className="tenant-subscription-actions">
                                <button
                                  type="button"
                                  className="tenant-subscription-edit-btn"
                                  onClick={() => handleEdit(sub)}
                                >
                                  Edit
                                </button>

                                <button
                                  type="button"
                                  className="tenant-subscription-delete-btn"
                                  onClick={() => handleDelete(sub)}
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="tenant-subscription-page-header">
                <div>
                  <h2>
                    {isEditing
                      ? "Edit Subscription"
                      : "Create Subscriptions"}
                  </h2>
                </div>

                <button
                  type="button"
                  className="tenant-subscription-back-btn"
                  onClick={handleCancel}
                >
                  ← Back
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                {formData.map((sub, index) => (
                  <div
                    className="tenant-subscription-card"
                    key={index}
                    style={{ marginBottom: "20px" }}
                  >
                    <div className="tenant-subscription-form-body">
                      <div className="tenant-subscription-record-header">
                        <h4>
                          {isEditing
                            ? "Subscription Details"
                            : `Subscription ${index + 1}`}
                        </h4>

                        {!isEditing && formData.length > 1 && (
                          <button
                            type="button"
                            className="tenant-subscription-remove-btn"
                            onClick={() => handleRemoveForm(index)}
                          >
                            Remove
                          </button>
                        )}
                      </div>

                      <div className="tenant-subscription-form-grid">
                        <div className="tenant-subscription-form-group">
                          <label>
                            Plan <span>*</span>
                          </label>
                          <select
                            name="plan"
                            value={sub.plan}
                            onChange={(e) => handleChange(index, e)}
                          >
                            <option value="">Select plan</option>
                            {PLAN_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="tenant-subscription-form-group">
                          <label>
                            Status <span>*</span>
                          </label>
                          <select
                            name="status"
                            value={sub.status}
                            onChange={(e) => handleChange(index, e)}
                          >
                            {STATUS_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="tenant-subscription-form-group">
                          <label>
                            Start Date <span>*</span>
                          </label>
                          <input
                            type="date"
                            name="start_date"
                            value={sub.start_date}
                            onChange={(e) => handleChange(index, e)}
                          />
                        </div>

                        <div className="tenant-subscription-form-group">
                          <label>Renewal Date</label>
                          <input
                            type="date"
                            name="renewal_date"
                            value={sub.renewal_date}
                            onChange={(e) => handleChange(index, e)}
                            min={sub.start_date || undefined}
                          />
                        </div>

                        <div className="tenant-subscription-form-group">
                          <label>Expiry Date</label>
                          <input
                            type="date"
                            name="expiry_date"
                            value={sub.expiry_date}
                            onChange={(e) => handleChange(index, e)}
                            min={sub.start_date || undefined}
                          />
                        </div>

                        <div className="tenant-subscription-form-group">
                          <label>
                            Seat Limit <span>*</span>
                          </label>
                          <input
                            type="number"
                            name="seat_limit"
                            value={sub.seat_limit}
                            onChange={(e) => handleChange(index, e)}
                            placeholder="e.g. 50"
                            min="0"
                            step="1"
                          />
                        </div>

                        <div className="tenant-subscription-form-group">
                          <label>Project Limit</label>
                          <input
                            type="number"
                            name="project_limit"
                            value={sub.project_limit}
                            onChange={(e) => handleChange(index, e)}
                            placeholder="e.g. 20 (optional)"
                            min="0"
                            step="1"
                          />
                        </div>

                        <div className="tenant-subscription-form-group">
                          <label>Storage Limit (GB)</label>
                          <input
                            type="number"
                            name="storage_limit_gb"
                            value={sub.storage_limit_gb}
                            onChange={(e) => handleChange(index, e)}
                            placeholder="e.g. 100 (optional)"
                            min="0"
                            step="1"
                          />
                        </div>

                        <div className="tenant-subscription-form-group">
                          <label>Used Seats</label>
                          <input
                            type="number"
                            name="used_seats"
                            value={sub.used_seats}
                            onChange={(e) => handleChange(index, e)}
                            placeholder="0"
                            min="0"
                            step="1"
                          />
                        </div>

                        <div className="tenant-subscription-form-group">
                          <label>Used Projects</label>
                          <input
                            type="number"
                            name="used_projects"
                            value={sub.used_projects}
                            onChange={(e) => handleChange(index, e)}
                            placeholder="0"
                            min="0"
                            step="1"
                          />
                        </div>

                        <div className="tenant-subscription-form-group">
                          <label>Used Storage (GB)</label>
                          <input
                            type="number"
                            name="used_storage_gb"
                            value={sub.used_storage_gb}
                            onChange={(e) => handleChange(index, e)}
                            placeholder="0"
                            min="0"
                            step="0.01"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {!isEditing && (
                  <button
                    type="button"
                    className="tenant-subscription-add-btn"
                    onClick={handleAddAnother}
                    style={{ marginBottom: "20px" }}
                  >
                    <span>+</span>
                    Add Another Subscription
                  </button>
                )}

                <div className="tenant-subscription-form-actions">
                  <button
                    type="button"
                    className="tenant-subscription-cancel-btn"
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="tenant-subscription-save-btn"
                    disabled={loading}
                  >
                    {loading
                      ? "Saving..."
                      : isEditing
                      ? "Update Subscription"
                      : "Save Subscriptions"}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddTenantSubscription;